/**
 * Tenant Context
 *
 * Provides tenant configuration, feature flags, and permission checks
 * throughout the application. This is the core of the Chameleon Architecture.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../services/supabase';
import { withSchemaRetry, SystemUpdatingError, getSupabaseErrorMessage } from '../services/supabaseErrors';
import {
  type TenantFeatureFlags,
  type TenantConfig,
  type TenantSettings,
  type RolePermissions,
  type RBACResource,
  type RBACAction,
  type DataScope,
  DEFAULT_FEATURE_FLAGS,
  DEFAULT_TENANT_SETTINGS,
  SYSTEM_ROLES,
  isFeatureEnabled as checkFeatureEnabled,
  hasPermission as checkPermission,
  mergePermissions,
} from '../config/tenant';

// ============================================
// TYPES
// ============================================

export interface TenantListItem {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  currency_code: string;
  locale: string;
  maturity_level: number;
  subscription_tier: string;
}

interface TenantState {
  config: TenantConfig | null;
  featureFlags: TenantFeatureFlags;
  permissions: RolePermissions;
  dataScope: DataScope;
  loading: boolean;
  error: string | null;
  // Super admin tenant switching
  allTenants: TenantListItem[];
  activeTenantId: string | null;
  viewAllTenants: boolean;
}

interface TenantContextType extends TenantState {
  // Feature flag checks
  isFeatureEnabled: (feature: keyof TenantFeatureFlags) => boolean;
  hasFeature: (feature: keyof TenantFeatureFlags) => boolean; // Alias

  // Permission checks
  can: (resource: RBACResource, action: RBACAction) => boolean;
  canAccess: (resource: RBACResource) => boolean;

  // Data scope helpers
  isAllDataScope: () => boolean;
  isAssignedStoresScope: () => boolean;
  getAssignedStoreIds: () => string[];

  // Refresh
  refreshConfig: () => Promise<void>;

  // Feature flag updates (for admin)
  updateFeatureFlags: (flags: Partial<TenantFeatureFlags>) => Promise<boolean>;

  // Maturity level updates (for admin)
  updateMaturityLevel: (level: number) => Promise<boolean>;

  // Combined update for both maturity and flags with cascade
  updateMaturityAndFlags: (level: number, flags: TenantFeatureFlags) => Promise<boolean>;

  // Tenant settings updates (for admin)
  updateTenantSettings: (settings: TenantSettings) => Promise<boolean>;

  // Tenant profile updates (for admin)
  updateTenantProfile: (profile: { name?: string; logo_url?: string; currency_code?: string; locale?: string }) => Promise<boolean>;

  // Super Admin tenant switching
  isSuperAdmin: boolean;
  setActiveTenant: (tenantId: string | null) => void;
  toggleViewAllTenants: () => void;
  refreshTenantList: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

export const TenantProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, tenant: authTenant } = useAuth();

  const [state, setState] = useState<TenantState>({
    config: null,
    featureFlags: DEFAULT_FEATURE_FLAGS,
    permissions: {},
    dataScope: 'assigned_stores',
    loading: true,
    error: null,
    // Super admin tenant switching
    allTenants: [],
    activeTenantId: null,
    viewAllTenants: false,
  });

  // Only true super_admin can switch tenants - tenant_admin is restricted to their own tenant
  const isSuperAdmin = authTenant?.role === 'super_admin';
  const isTenantAdmin = authTenant?.role === 'tenant_admin';

  // Fetch all tenants (super admin only)
  const fetchAllTenants = useCallback(async () => {
    if (!isSuperAdmin) return;

    try {
      const { data, error: fetchError } = await supabase
        .from('tenants')
        .select('id, name, slug, logo_url, currency_code, locale, maturity_level, subscription_tier')
        .order('name', { ascending: true });

      if (fetchError) {
        if (import.meta.env.DEV) console.error('Failed to fetch tenants:', fetchError);
        return;
      }

      setState(s => ({ ...s, allTenants: data || [] }));
    } catch (err) {
      if (import.meta.env.DEV) console.error('Failed to fetch tenants:', err);
    }
  }, [isSuperAdmin]);

  // Set active tenant for data filtering
  const setActiveTenant = useCallback((tenantId: string | null) => {
    if (!isSuperAdmin && tenantId !== authTenant?.id) {
      if (import.meta.env.DEV) console.warn('Non-super-admin cannot switch tenants');
      return;
    }

    setState(s => ({
      ...s,
      activeTenantId: tenantId,
      viewAllTenants: tenantId === null,
    }));
  }, [isSuperAdmin, authTenant?.id]);

  // Toggle view all tenants mode
  const toggleViewAllTenants = useCallback(() => {
    if (!isSuperAdmin) return;

    setState(s => ({
      ...s,
      viewAllTenants: !s.viewAllTenants,
      activeTenantId: s.viewAllTenants ? authTenant?.id || null : null,
    }));
  }, [isSuperAdmin, authTenant?.id]);

  // Fetch full tenant config when auth tenant changes
  const fetchTenantConfig = useCallback(async () => {
    if (!authTenant?.id) {
      setState(s => ({
        ...s,
        loading: false,
        config: null,
        featureFlags: DEFAULT_FEATURE_FLAGS,
      }));
      return;
    }

    setState(s => ({ ...s, loading: true, error: null }));

    try {
      // Fetch tenant config
      const { data: tenantData, error: tenantError } = await supabase
        .from('tenants')
        .select('*')
        .eq('id', authTenant.id)
        .single();

      if (tenantError) throw tenantError;

      // Fetch user's role and permissions
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('email', user?.email)
        .single();

      // Parse feature flags from tenant config
      const featureFlags: TenantFeatureFlags = {
        ...DEFAULT_FEATURE_FLAGS,
        ...(tenantData?.feature_flags as unknown as Partial<TenantFeatureFlags> || {}),
      };

      // Get permissions from role or system role
      let permissions: RolePermissions = {};
      let dataScope: DataScope = 'assigned_stores';

      const userRole = userData?.role || authTenant.role;
      if (userRole && SYSTEM_ROLES[userRole]) {
        permissions = SYSTEM_ROLES[userRole].permissions;
        dataScope = SYSTEM_ROLES[userRole].dataScope;
      }

      // Merge with user-specific overrides if available
      const userPermissions = userData?.permissions;
      if (userPermissions && typeof userPermissions === 'object') {
        permissions = mergePermissions(permissions, userPermissions as unknown as RolePermissions);
      }

      const config: TenantConfig = {
        id: tenantData.id,
        name: tenantData.name,
        slug: tenantData.slug,
        logoUrl: tenantData.logo_url,
        currencyCode: tenantData.currency_code || 'USD',
        locale: tenantData.locale || 'es-CL',
        maturityLevel: tenantData.maturity_level || 1,
        featureFlags,
        subscriptionTier: (tenantData.subscription_tier || 'starter') as TenantConfig['subscriptionTier'],
        config: (tenantData.config || { hasERP: false, hasPOS: false, hasVMS: false }) as unknown as TenantConfig['config'],
        themeConfig: (tenantData.theme_config || {}) as TenantConfig['themeConfig'],
        billingConfig: (tenantData.billing_config || {}) as TenantConfig['billingConfig'],
        tenantSettings: (tenantData.tenant_settings || {}) as TenantSettings,
      };

      setState(prev => ({
        ...prev,
        config,
        featureFlags,
        permissions,
        dataScope,
        loading: false,
        error: null,
      }));
    } catch (e) {
      if (import.meta.env.DEV) console.error('Failed to fetch tenant config:', e);

      // Fallback to demo config
      const demoConfig: TenantConfig = {
        id: authTenant.id,
        name: authTenant.name,
        slug: authTenant.slug,
        currencyCode: 'USD',
        locale: 'es-CL',
        maturityLevel: authTenant.maturity_level || 5,
        featureFlags: DEFAULT_FEATURE_FLAGS,
        subscriptionTier: 'enterprise',
        config: { hasERP: false, hasPOS: false, hasVMS: false },
        themeConfig: {},
        billingConfig: {},
        tenantSettings: {},
      };

      const demoPermissions = SYSTEM_ROLES[authTenant.role || 'super_admin']?.permissions || {};
      const demoScope = SYSTEM_ROLES[authTenant.role || 'super_admin']?.dataScope || 'all';

      setState(prev => ({
        ...prev,
        config: demoConfig,
        featureFlags: DEFAULT_FEATURE_FLAGS,
        permissions: demoPermissions,
        dataScope: demoScope,
        loading: false,
        error: null,
      }));
    }
  }, [authTenant, user]);

  useEffect(() => {
    fetchTenantConfig();
    // Set initial active tenant
    if (authTenant?.id) {
      setState(s => ({ ...s, activeTenantId: authTenant.id }));
    }
    // Fetch all tenants for super admin
    if (isSuperAdmin) {
      fetchAllTenants();
    }
  }, [fetchTenantConfig, authTenant?.id, isSuperAdmin, fetchAllTenants]);

  // ============================================
  // FEATURE FLAG CHECKS
  // ============================================

  const isFeatureEnabled = useCallback((feature: keyof TenantFeatureFlags): boolean => {
    if (!state.config) return false;
    return checkFeatureEnabled(
      { featureFlags: state.featureFlags, maturityLevel: state.config.maturityLevel },
      feature
    );
  }, [state.config, state.featureFlags]);

  // ============================================
  // PERMISSION CHECKS
  // ============================================

  const can = useCallback((resource: RBACResource, action: RBACAction): boolean => {
    return checkPermission(state.permissions, resource, action);
  }, [state.permissions]);

  const canAccess = useCallback((resource: RBACResource): boolean => {
    return checkPermission(state.permissions, resource, 'read');
  }, [state.permissions]);

  // ============================================
  // DATA SCOPE HELPERS
  // ============================================

  const isAllDataScope = useCallback(() => state.dataScope === 'all', [state.dataScope]);
  const isAssignedStoresScope = useCallback(() => state.dataScope === 'assigned_stores', [state.dataScope]);

  const getAssignedStoreIds = useCallback((): string[] => {
    // This would come from the user record
    // For now return empty (meaning all stores if scope is 'all')
    return [];
  }, []);

  // ============================================
  // ADMIN FUNCTIONS
  // ============================================

  const updateFeatureFlags = useCallback(async (flags: Partial<TenantFeatureFlags>): Promise<boolean> => {
    if (!state.config?.id) {
      if (import.meta.env.DEV) console.error('updateFeatureFlags: No tenant config ID available');
      return false;
    }

    const tenantId = state.config.id;
    const newFlags = { ...state.featureFlags, ...flags };

    try {
      if (import.meta.env.DEV) console.log('updateFeatureFlags: Updating tenant', tenantId, 'with flags:', newFlags);

      // Use withSchemaRetry to handle PGRST205 errors gracefully
      const data = await withSchemaRetry(async () => {
        const result = await supabase
          .from('tenants')
          .update({ feature_flags: newFlags })
          .eq('id', tenantId)
          .select();

        if (result.error) {
          if (import.meta.env.DEV) console.error('updateFeatureFlags: Supabase error:', result.error);
          throw result.error;
        }
        return result.data;
      });

      if (import.meta.env.DEV) console.log('updateFeatureFlags: Success, updated data:', data);

      setState(s => ({
        ...s,
        featureFlags: newFlags,
        config: s.config ? { ...s.config, featureFlags: newFlags } : null,
      }));

      return true;
    } catch (e) {
      // Handle SystemUpdatingError with user-friendly message
      if (import.meta.env.DEV) {
        if (e instanceof SystemUpdatingError) {
          console.warn('updateFeatureFlags: System updating, retry suggested:', e.message);
        } else {
          console.error('Failed to update feature flags:', e);
        }
      }
      return false;
    }
  }, [state.config?.id, state.featureFlags]);

  const updateMaturityLevel = useCallback(async (level: number): Promise<boolean> => {
    if (!state.config?.id) return false;

    const tenantId = state.config.id;

    try {
      await withSchemaRetry(async () => {
        const result = await supabase
          .from('tenants')
          .update({ maturity_level: level })
          .eq('id', tenantId);

        if (result.error) throw result.error;
        return result.data;
      });

      setState(s => ({
        ...s,
        config: s.config ? { ...s.config, maturityLevel: level } : null,
      }));

      return true;
    } catch (e) {
      if (import.meta.env.DEV) {
        if (e instanceof SystemUpdatingError) {
          console.warn('updateMaturityLevel: System updating, retry suggested:', e.message);
        } else {
          console.error('Failed to update maturity level:', e);
        }
      }
      return false;
    }
  }, [state.config?.id]);

  const updateMaturityAndFlags = useCallback(async (level: number, flags: TenantFeatureFlags): Promise<boolean> => {
    if (!state.config?.id) return false;

    const tenantId = state.config.id;

    try {
      await withSchemaRetry(async () => {
        const result = await supabase
          .from('tenants')
          .update({
            maturity_level: level,
            feature_flags: flags
          })
          .eq('id', tenantId);

        if (result.error) throw result.error;
        return result.data;
      });

      setState(s => ({
        ...s,
        featureFlags: flags,
        config: s.config ? { ...s.config, maturityLevel: level, featureFlags: flags } : null,
      }));

      return true;
    } catch (e) {
      if (import.meta.env.DEV) {
        if (e instanceof SystemUpdatingError) {
          console.warn('updateMaturityAndFlags: System updating, retry suggested:', e.message);
        } else {
          console.error('Failed to update maturity and flags:', e);
        }
      }
      return false;
    }
  }, [state.config?.id]);

  const updateTenantSettings = useCallback(async (settings: TenantSettings): Promise<boolean> => {
    if (!state.config?.id) {
      if (import.meta.env.DEV) console.error('updateTenantSettings: No tenant config ID available');
      return false;
    }

    const tenantId = state.config.id;

    try {
      await withSchemaRetry(async () => {
        const result = await supabase
          .from('tenants')
          .update({ tenant_settings: settings })
          .eq('id', tenantId);

        if (result.error) throw result.error;
        return result.data;
      });

      setState(s => ({
        ...s,
        config: s.config ? { ...s.config, tenantSettings: settings } : null,
      }));

      return true;
    } catch (e) {
      if (import.meta.env.DEV) {
        if (e instanceof SystemUpdatingError) {
          console.warn('updateTenantSettings: System updating, retry suggested:', e.message);
        } else {
          console.error('Failed to update tenant settings:', e);
        }
      }
      return false;
    }
  }, [state.config?.id]);

  const updateTenantProfile = useCallback(async (profile: { name?: string; logo_url?: string; currency_code?: string; locale?: string }): Promise<boolean> => {
    if (!state.config?.id) {
      if (import.meta.env.DEV) console.error('updateTenantProfile: No tenant config ID available');
      return false;
    }

    const tenantId = state.config.id;

    try {
      await withSchemaRetry(async () => {
        const result = await supabase
          .from('tenants')
          .update(profile)
          .eq('id', tenantId);

        if (result.error) throw result.error;
        return result.data;
      });

      setState(s => ({
        ...s,
        config: s.config ? {
          ...s.config,
          ...(profile.name !== undefined && { name: profile.name }),
          ...(profile.logo_url !== undefined && { logoUrl: profile.logo_url }),
          ...(profile.currency_code !== undefined && { currencyCode: profile.currency_code }),
          ...(profile.locale !== undefined && { locale: profile.locale }),
        } : null,
      }));

      return true;
    } catch (e) {
      if (import.meta.env.DEV) {
        if (e instanceof SystemUpdatingError) {
          console.warn('updateTenantProfile: System updating, retry suggested:', e.message);
        } else {
          console.error('Failed to update tenant profile:', e);
        }
      }
      return false;
    }
  }, [state.config?.id]);

  // ============================================
  // CONTEXT VALUE
  // ============================================

  const value = useMemo<TenantContextType>(() => ({
    ...state,
    isFeatureEnabled,
    hasFeature: isFeatureEnabled,
    can,
    canAccess,
    isAllDataScope,
    isAssignedStoresScope,
    getAssignedStoreIds,
    refreshConfig: fetchTenantConfig,
    updateFeatureFlags,
    updateMaturityLevel,
    updateMaturityAndFlags,
    updateTenantSettings,
    updateTenantProfile,
    // Super admin tenant switching
    isSuperAdmin,
    setActiveTenant,
    toggleViewAllTenants,
    refreshTenantList: fetchAllTenants,
  }), [
    state,
    isFeatureEnabled,
    can,
    canAccess,
    isAllDataScope,
    isAssignedStoresScope,
    getAssignedStoreIds,
    fetchTenantConfig,
    updateFeatureFlags,
    updateMaturityLevel,
    updateMaturityAndFlags,
    updateTenantSettings,
    updateTenantProfile,
    isSuperAdmin,
    setActiveTenant,
    toggleViewAllTenants,
    fetchAllTenants,
  ]);

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
};

// ============================================
// HOOKS
// ============================================

/**
 * Access the full tenant context.
 */
export const useTenant = (): TenantContextType => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};

/**
 * Check if a specific feature is enabled.
 */
export const useFeature = (feature: keyof TenantFeatureFlags): boolean => {
  const { isFeatureEnabled } = useTenant();
  return isFeatureEnabled(feature);
};

/**
 * Check if user has permission for an action on a resource.
 */
export const usePermission = (resource: RBACResource, action: RBACAction): boolean => {
  const { can } = useTenant();
  return can(resource, action);
};

/**
 * Get all feature flags as an object.
 */
export const useFeatureFlags = (): TenantFeatureFlags => {
  const { featureFlags } = useTenant();
  return featureFlags;
};

export default TenantContext;
